import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import ClampLines from "../../components/app/ClampLines";
import { useTranslation } from "react-i18next";

function measureOverflow(el: HTMLElement): boolean {
  // -webkit-line-clamp makes scrollHeight unreliable (equals clientHeight on WebKit).
  // Temporarily remove clamping to measure the true content height.
  const savedDisplay = el.style.display;
  const savedClamp = el.style.webkitLineClamp;
  const savedOverflow = el.style.overflow;

  el.style.display = "block";
  el.style.webkitLineClamp = "unset";
  el.style.overflow = "visible";
  const fullHeight = el.scrollHeight;

  el.style.display = savedDisplay;
  el.style.webkitLineClamp = savedClamp;
  el.style.overflow = savedOverflow;

  return fullHeight > el.clientHeight;
}

export default function ExpandableText({
  children,
  isExpanded,
  setExpand,
  lines,
  variant = "text",
}: {
  children: ReactNode;
  isExpanded: boolean;
  setExpand: (expanded: boolean) => void;
  /**
   * Collapsed clamp height. REQUIRED, not defaulted to the historical 10: a default
   * would let a new call site silently inherit a clamp nobody chose for it, and the
   * whole point of the room header is that 2 is a design decision. Making it required
   * forces every existing call site to be visited (AGENTS.md §"No Default Parameters
   * for Refactors").
   */
  lines: number;
  variant?: "text" | "list";
}) {
  const { t } = useTranslation();
  const clampRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = clampRef.current;
    if (!el || isExpanded) return;

    setIsOverflowing(measureOverflow(el));

    const observer = new ResizeObserver(() => {
      setIsOverflowing(measureOverflow(el));
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [children, isExpanded]);

  const showButton = isOverflowing || isExpanded;

  return (
    <>
      <ClampLines ref={clampRef} lines={isExpanded ? 0 : lines}>
        {variant === "list" ? (
          children
        ) : (
          <p className="mb-0 whitespace-pre-wrap">{children}</p>
        )}
      </ClampLines>
      {showButton && (
        <button
          type="button"
          aria-expanded={isExpanded}
          className="mt-[10px] p-0 bg-transparent border-0 outline-none shadow-none cursor-pointer flex items-center gap-[4px] text-[14px] font-medium leading-[20px] text-[#155dfc] tracking-[-0.15px]"
          onClick={() => {
            setExpand(!isExpanded);
          }}
        >
          {isExpanded ? t("Close") : t("Read more")}
          <svg
            className={cn(
              "size-[16px] transition-transform",
              isExpanded && "rotate-180"
            )}
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </>
  );
}
